import { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { UserPlus } from 'lucide-react';

import { animatedModal, AnimatedModalProps, StyledChipInput, CopyableInput, Modal } from '@labradorsports/components';
import { ProfileFlags, JoinTypes } from '@labradorsports/constants';
import { useSite, validateEmail } from '@labradorsports/utils';

import {
    useInviteMembersMutation,
    useRetrieveRosterQuery,
    useUpdateProfileFlagMutation,
} from '../../../store/index.js';
import InvitePreview from '../invite-preview/invite-preview.js';

interface OwnProps {
    team: any;
}

type Props = OwnProps & AnimatedModalProps;

const InvitePlayersModal: FC<Props> = ({ team, onClose, open }) => {
    const [inviteMembers] = useInviteMembersMutation();
    const [setProfileFlag] = useUpdateProfileFlagMutation();

    const { data: { roster = [] } = {} } = useRetrieveRosterQuery({ teamId: team.id });

    const { Config } = useSite();
    const [playerEmails, setPlayerEmails] = useState([]);
    const [showError, setShowError] = useState(false);
    const [showingInvitePreview, setShowingInvitePreview] = useState(false);

    useEffect(() => {
        if (open) {
            setProfileFlag({ flag: ProfileFlags.INVITED_TEAM, value: true });
        }
    }, [open]);

    const prefillUnregistered = () => {
        const unregistered = roster.filter((player) => !player.uid && player.email).map((player) => player.email);

        setPlayerEmails(unregistered);
    };

    useEffect(() => {
        if (open) {
            prefillUnregistered();
        }
    }, [open, team.id, roster.map((player) => player.email).join(',')]);

    const onPlayersChange = (event: any, val: string[]) => {
        setShowError(false);
        setPlayerEmails(val);
    };

    const sendEmails = async () => {
        if (playerEmails.length === 0) {
            setShowError(true);
            return;
        }

        try {
            await inviteMembers({
                entityId: team.id,
                playerEmails,
            });
        } catch (e) {
            // Do nothing
        }

        onClose();
    };

    const showEmailPreview = () => {
        setShowingInvitePreview(true);
    };

    const hideEmailPreview = () => {
        setShowingInvitePreview(false);
    };

    if (!team) return null;

    const publicPlayerUrl = `${Config.SiteConfig.Url}/join-team?k=${team.playerJoinCode}&j=${team.id}`;

    const buttons = [
        { text: 'Cancel' },
        {
            text: 'Send Email',
            variant: 'contained',
            color: 'primary',
            action: sendEmails,
        },
    ];

    return (
        <Modal title="Invite Players" icon={<UserPlus />} buttons={buttons} onClose={onClose}>
            <p>
                Send an invitation email to your players and contacts by filling the input box with their email
                addresses.
            </p>
            <StyledChipInput
                value={playerEmails}
                onChange={onPlayersChange}
                validateValue={validateEmail}
                label="Player Emails"
                style={{ marginBottom: '6px' }}
                fullWidth
            />
            {showError ? <p className="text-danger small">Please enter at least one email address.</p> : undefined}
            <p>This box is prefilled with any emails in your roster that have not created accounts.</p>
            {showingInvitePreview ? (
                <>
                    <InvitePreview inviteType={JoinTypes.PLAYER} team={team} />
                    <div className="text-center">
                        <Button onClick={hideEmailPreview}>Hide Email Preview</Button>
                    </div>
                </>
            ) : (
                <div className="text-center">
                    <Button onClick={showEmailPreview}>Show Email Preview</Button>
                </div>
            )}
            <div className="modal-section">
                <p>Or share this team link or Team Key to any players or parents you want on your team. </p>
                <p>Team Key: {team.playerJoinCode}</p>
                <CopyableInput label="Player Invite Link" value={publicPlayerUrl} />
                <div className="text-end">
                    <Button className="invite-how-does-this-work">How does this work?</Button>
                </div>
            </div>
        </Modal>
    );
};

export default animatedModal<Props>(InvitePlayersModal);
